"use client";

import type { ProductCard } from "@ellaandil/formatters";
import { useEffect, useState } from "react";
import { QuickAddPanel } from "./QuickAddPanel";

type QuickAddProduct = ProductCard & { depictProductId?: string };

/**
 * Animated mount/unmount wrapper for the QuickAddPanel. Uses the pure-CSS
 * grid-rows-[0fr]→[1fr] collapse trick so the panel slides open/closed
 * without a JS animation library, and latches onto the last product seen so
 * the exit animation still has content to render after `product` clears.
 *
 * Shared between `RecommendationsCarousel` (cart upsell + PDP recommendations)
 * and `CartRecentlyViewed` (recently-viewed row list) — same inline
 * quick-add behavior regardless of how the products themselves are laid out.
 */
export const QuickAddSlot = <T extends QuickAddProduct>({
	product,
	onClose,
}: {
	product: T | undefined;
	onClose: () => void;
}) => {
	// Two pieces of state, intentionally decoupled:
	//   • `rendered` controls what's mounted (latches onto the last product so
	//     the exit animation has content to display after `product` clears)
	//   • `visible` drives the `data-state` attribute that the CSS transition
	//     keys off — flipped one frame after mount so the enter transition
	//     actually plays instead of snapping straight to the open state.
	const [rendered, setRendered] = useState<T | undefined>(undefined);
	const [visible, setVisible] = useState(false);

	useEffect(() => {
		if (product) {
			setRendered(product);
			const raf = requestAnimationFrame(() => setVisible(true));
			return () => cancelAnimationFrame(raf);
		}
		setVisible(false);
	}, [product]);

	if (!rendered) return null;

	return (
		<div
			data-state={visible ? "open" : "closed"}
			className="grid grid-rows-[0fr] opacity-0 transition-[grid-template-rows,opacity] duration-200 ease-out data-[state=open]:grid-rows-[1fr] data-[state=open]:opacity-100"
			onTransitionEnd={(e) => {
				// Only react to the grid-template-rows transition (opacity finishes
				// in the same frame and would otherwise fire a premature unmount).
				if (e.propertyName !== "grid-template-rows") return;
				if (!visible) setRendered(undefined);
			}}
		>
			<div className="overflow-hidden">
				<QuickAddPanel key={rendered.id} product={rendered} onClose={onClose} />
			</div>
		</div>
	);
};
